import { useNavigate } from 'react-router-dom'

import { Box, ProgressBar, Spinner, Table } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import AnalyticsDescription from 'modules/engagement_insights/components/_Widget/_AnalyticsDescription'
import EmptyStatusDescription from 'modules/engagement_insights/components/_Widget/_EmptyStatusDescription'
import WidgetHeader from 'modules/engagement_insights/components/_Widget/_Header'
import {
  SurveyCategoryScores,
  useClimateSurveyInsight,
} from 'modules/engagement_insights/hooks/useClimateSurveyInsight'
import { useEngagementInsightsData } from 'modules/engagement_insights/hooks/useEngagementInsightsData'
import { getTagColor, getTagLabel } from 'modules/engagement_insights/lib/surveyStatusTag'
import {
  getClimateScoreDescription,
  getClimateWidgetEllipseColor,
} from 'modules/engagement_insights/lib/widget'

const ClimateChartBars = ({
  surveyCategoryScores,
  showScore,
}: {
  surveyCategoryScores: SurveyCategoryScores
  showScore: boolean
}) => {
  return (
    <Table fullWidth hideBorder style={{ fontSize: '16px', fontWeight: '500' }}>
      <Table.Body>
        {surveyCategoryScores.map(({ name, score }) => {
          return (
            <Table.Row key={name}>
              <Table.Cell noBorder padding="s2">
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {name}
                </div>
              </Table.Cell>
              <Table.Cell padding="s4" noBorder>
                <Box gap={'s8'} flexDirection="row" paddingLeft={'s32'}>
                  <ProgressBar
                    amount={score || 0}
                    total={10}
                    progressColor="viridian1000"
                    barColor="viridian100"
                    ariaLabel={name}
                  />
                  {showScore && <div style={{ width: '30px', textAlign: 'right' }}>{score}</div>}
                </Box>
              </Table.Cell>
            </Table.Row>
          )
        })}
      </Table.Body>
    </Table>
  )
}

const ClimateWidgetStats = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { climate, managerId } = useEngagementInsightsData()

  const { loading, climateSurveyInsight } = useClimateSurveyInsight({ managerId })

  const {
    score = 0,
    respondents = 0,
    participants = 0,
    surveyCategoryScores = [],
  } = climateSurveyInsight || {}

  const tagColor = getTagColor(climate)
  const tagLabel = getTagLabel(climate)

  const hasEnoughAnswers = climateSurveyInsight?.enoughAnswers ?? false
  const showEllipse = hasEnoughAnswers && climate?.isActive

  const ellipseGradientColor =
    showEllipse && !!score ? getClimateWidgetEllipseColor(score) : undefined
  const scoreDescription = score ? getClimateScoreDescription(score) : ''
  const surveyId = climate?.attributes?.id

  const handleClick = () => {
    navigate(navigation.engagement.surveys.id.analytics.fullPath({ id: surveyId }))
    track('Go to Climate')
  }

  return (
    <Box
      borderRadius={{
        all: 'abs008',
      }}
      border={{
        all: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
      style={{ flex: '1 1 0', position: 'relative', overflow: 'hidden' }}
      padding="s24"
      cursor="pointer"
      onClick={() => (surveyId ? handleClick() : null)}
    >
      <WidgetHeader
        loading={loading}
        showEllipse={showEllipse}
        showTag={!showEllipse}
        tagColor={tagColor}
        tagLabel={tagLabel}
        title={i18n.t('engagement_insights.climate_widget.analytics.title')}
        ellipseGradientColor={ellipseGradientColor}
      />
      {loading && (
        <Box alignItems="center" justifyContent="center" height="full">
          <Spinner />
        </Box>
      )}
      {!loading && (
        <>
          {hasEnoughAnswers && score ? (
            <AnalyticsDescription
              participants={participants}
              respondents={respondents}
              score={score}
              scoreDescription={scoreDescription}
              showMaxScore
            />
          ) : (
            <EmptyStatusDescription respondents={respondents} participants={participants} />
          )}
          {!!surveyCategoryScores.length && (
            <Box
              flexDirection="column"
              style={{ position: 'absolute', bottom: '24px', left: '24px', right: '24px' }}
            >
              <ClimateChartBars
                surveyCategoryScores={surveyCategoryScores}
                showScore={hasEnoughAnswers}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default observer(ClimateWidgetStats)
